import React, { useState, useCallback } from "react";
import { HashRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ThemeProvider } from "styled-components";
import GlobalStyles from "./styles/GlobalStyles";
import { buildTheme } from "./styles/theme";
import Splash from "./components/Splash";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import About from "./components/About";
// import FormerPosts from "./components/FormerPosts";
import HomePage from "./components/Home/HomePage";
import Skills from "./components/Skills";
import Experience from "./components/Experience";
import Projects from "./components/Projects";
import Education from "./components/Education";
import LeetCode from "./components/LeetCode";
import Contact from "./components/Contact";
import OpenSource from "./components/OpenSource";
import Footer from "./components/Footer";
import PageMeta from "./components/PageMeta";
import NotFound from "./components/NotFound";
import MaraudersMap from "./components/MaraudersMap";
import SpellCaster from "./components/SpellCaster";
import FrogCard from "./components/FrogCard";
import ResumeRibbon from "./components/ResumeRibbon";
import MagicCursor from "./components/MagicCursor";
import WorldIndicator from "./components/WorldIndicator";
import MusicToggle from "./components/MusicToggle";
import { useWorldMusic } from "./hooks/useWorldMusic";
import SectionNav from "./components/SectionNav";

/* ── Page transition wrapper ── */
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.3 } },
};

const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
    {children}
  </motion.div>
);

/* ── Animated routes ── */
const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Page><Hero />{/* <FormerPosts /> */}<About /><HomePage /></Page>} />
        <Route path="/skills" element={<Page><Skills /></Page>} />
        <Route path="/experience" element={<Page><Experience /></Page>} />
        <Route path="/leetcode" element={<Page><LeetCode /></Page>} />
        <Route path="/projects" element={<Page><Projects /></Page>} />
        <Route path="/education" element={<Page><Education /></Page>} />
        <Route path="/opensource" element={<Page><OpenSource /></Page>} />
        <Route path="/contact" element={<Page><Contact /></Page>} />
        <Route path="*" element={<Page><NotFound /></Page>} />
      </Routes>
    </AnimatePresence>
  );
};

/* ── Music controller (must be inside Router) ── */
const WorldMusic: React.FC = () => {
  const { muted, toggleMute, currentWorld } = useWorldMusic();
  return <MusicToggle muted={muted} onToggle={toggleMute} world={currentWorld} />;
};

function App() {
  const [splashDone, setSplashDone] = useState(false);

  const handleSplashComplete = useCallback(() => {
    setSplashDone(true);
  }, []);

  return (
    <ThemeProvider theme={buildTheme("gryffindor")}>
      <GlobalStyles />
      <MagicCursor />
      {!splashDone && <Splash onComplete={handleSplashComplete} />}
      {splashDone && (
        <HashRouter>
          <PageMeta />
          <Navbar />
          <WorldIndicator />
          <WorldMusic />
          <ResumeRibbon />
          <SectionNav />
          <MaraudersMap />
          <SpellCaster />
          <FrogCard />
          <main>
            <AnimatedRoutes />
          </main>
          <Footer />
        </HashRouter>
      )}
    </ThemeProvider>
  );
}

export default App;
